/**
 * Main-thread client: packs a SurfaceRequest into compact transferable arrays (so the model's own buffers are never
 * detached), runs it in a module Worker (or in-thread when Worker is unavailable or blocked), remaps atomIndex back
 * to model indices, and keeps an LRU cache keyed by a hash of (atomIndices, coordinates of those atoms, probe,
 * density, exteriorOnly, keepAtoms) so toggling a surface twice is instant.
 */
import type { SurfaceMesh, SurfaceRequest, SurfaceRequestData } from './types';
import type { FromWorker, ToWorker } from './worker';

/** Main-thread entry ('@nanome-lite/surface/client'): the client, the request builders and the types; no SESAM, no Molstar. */
export * from './types';
export { radiiFromModel, wholeChainAtoms, subsurfaceRequest } from './request';

/** Same error the worker side raises (edt.ts abortError), without importing that module on the main thread. */
function abortError(): Error {
  const e = new Error('surface computation aborted');
  e.name = 'AbortError';
  return e;
}

export interface SurfaceClientOptions {
  /** a Worker, a factory for one, or false to force in-thread computation; default: new Worker(./worker.ts) */
  worker?: Worker | (() => Worker) | false;
  /** URL of sesam.wasm when it is not served next to sesam.mjs (bundlers, extensions) */
  wasmUrl?: string;
  /** pre-fetched sesam.wasm bytes (copied to the worker) */
  wasmBinary?: ArrayBuffer;
  /** LRU limits; default 32 meshes / 256 MB */
  cacheEntries?: number;
  cacheBytes?: number;
}

export interface SurfaceClientStats {
  hits: number;
  misses: number;
  entries: number;
  bytes: number;
}

export interface SurfaceClient {
  readonly mode: 'worker' | 'inline';
  /** resolves with a mesh whose buffers are shared with the cache: treat them as read-only */
  compute(req: SurfaceRequest, options?: { onProgress?: (done: number, total: number) => void }): Promise<SurfaceMesh>;
  /** cache lookup only */
  peek(req: SurfaceRequest): SurfaceMesh | undefined;
  /** cache key for a request (what compute/peek hash) */
  keyOf(req: SurfaceRequest): string;
  clearCache(): void;
  readonly stats: SurfaceClientStats;
  /** cancels every pending job, terminates the worker, empties the cache */
  dispose(): void;
}

interface Packed {
  key: string;
  data: SurfaceRequestData;
  transfer: ArrayBuffer[];
  /** compact index -> model atom index */
  atomIndices: Uint32Array;
}

/**
 * Gathers the requested atoms into compact arrays. The worker computes over compact indices 0..n-1; the client maps
 * each vertex's atomIndex back through `atomIndices`.
 */
export function packRequest(req: SurfaceRequest): Packed {
  const n = req.atomIndices.length;
  const xyz = new Float32Array(3 * n), radius = new Float32Array(n), chainIndex = new Uint32Array(n);
  const posOf = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    const m = req.atomIndices[i]!;
    xyz[3 * i] = req.xyz[3 * m]!; xyz[3 * i + 1] = req.xyz[3 * m + 1]!; xyz[3 * i + 2] = req.xyz[3 * m + 2]!;
    radius[i] = req.radius[m]!;
    chainIndex[i] = req.chainIndex[m]!;
    posOf.set(m, i);
  }
  const atomIndices = new Uint32Array(n);
  for (let i = 0; i < n; i++) atomIndices[i] = i;
  let keepAtoms: Uint32Array | undefined;
  if (req.keepAtoms) {
    const keep: number[] = [];
    for (let i = 0; i < req.keepAtoms.length; i++) {
      const p = posOf.get(req.keepAtoms[i]!);
      if (p !== undefined) keep.push(p);
    }
    keep.sort((a, b) => a - b);
    keepAtoms = Uint32Array.from(keep);
  }
  const data: SurfaceRequestData = { xyz, radius, chainIndex, atomIndices };
  if (keepAtoms) data.keepAtoms = keepAtoms;
  if (req.probe !== undefined) data.probe = req.probe;
  if (req.density !== undefined) data.density = req.density;
  if (req.exteriorOnly !== undefined) data.exteriorOnly = req.exteriorOnly;
  if (req.forceEdt !== undefined) data.forceEdt = req.forceEdt;
  if (req.maxOpenEdges !== undefined) data.maxOpenEdges = req.maxOpenEdges;

  const h = new Hasher();
  h.u32(req.atomIndices); h.f32(xyz); h.f32(radius); h.u32(chainIndex);
  h.num(req.probe ?? -1); h.num(req.density ?? -1); h.num(req.exteriorOnly === false ? 0 : 1); h.num(req.forceEdt ? 1 : 0); h.num(req.maxOpenEdges ?? -1);
  h.num(keepAtoms ? 1 : 0); if (keepAtoms) h.u32(keepAtoms);
  // Fresh typed arrays above, so these are plain ArrayBuffers (never shared memory).
  const transfer = [xyz.buffer, radius.buffer, chainIndex.buffer, atomIndices.buffer] as ArrayBuffer[];
  if (keepAtoms) transfer.push(keepAtoms.buffer as ArrayBuffer);
  return { key: h.hex(), data, transfer, atomIndices: req.atomIndices.slice() };
}

/** Two-lane FNV-1a over 32-bit words; 64 bits of key is plenty for a cache of a few dozen meshes. */
export class Hasher {
  private a = 0x811c9dc5 >>> 0;
  private b = 0x050c5d1f >>> 0;
  private word(w: number) {
    this.a = Math.imul((this.a ^ (w & 0xff)) >>> 0, 0x01000193) >>> 0;
    this.a = Math.imul((this.a ^ ((w >>> 8) & 0xff)) >>> 0, 0x01000193) >>> 0;
    this.a = Math.imul((this.a ^ ((w >>> 16) & 0xff)) >>> 0, 0x01000193) >>> 0;
    this.a = Math.imul((this.a ^ (w >>> 24)) >>> 0, 0x01000193) >>> 0;
    this.b = Math.imul((this.b ^ w) >>> 0, 0x9e3779b1) >>> 0;
    this.b ^= this.b >>> 15;
  }
  u32(arr: Uint32Array) { this.word(arr.length); for (let i = 0; i < arr.length; i++) this.word(arr[i]!); return this; }
  f32(arr: Float32Array) { return this.u32(new Uint32Array(arr.buffer, arr.byteOffset, arr.length)); }
  num(x: number) { const f = new Float64Array([x]); return this.u32(new Uint32Array(f.buffer)); }
  hex() { return this.a.toString(16).padStart(8, '0') + this.b.toString(16).padStart(8, '0'); }
}

function meshBytes(m: SurfaceMesh): number {
  return m.position.byteLength + m.normal.byteLength + m.atomIndex.byteLength + m.triangles.byteLength;
}

interface Waiter {
  resolve: (m: SurfaceMesh) => void;
  reject: (e: Error) => void;
  onProgress?: ((done: number, total: number) => void) | undefined;
  signal?: AbortSignal | undefined;
  onAbort?: () => void;
}

interface Job {
  id: number;
  key: string;
  atomIndices: Uint32Array;
  waiters: Set<Waiter>;
}

export function createSurfaceClient(options: SurfaceClientOptions = {}): SurfaceClient {
  const maxEntries = options.cacheEntries ?? 32;
  const maxBytes = options.cacheBytes ?? 256 * 1024 * 1024;
  const cache = new Map<string, SurfaceMesh>();
  let cacheBytes = 0;
  const stats: SurfaceClientStats = { hits: 0, misses: 0, entries: 0, bytes: 0 };
  const jobs = new Map<number, Job>();
  const byKey = new Map<string, Job>();
  let nextId = 1;
  let disposed = false;

  const onMessage = (msg: FromWorker) => {
    const job = jobs.get(msg.id);
    if (!job) return;
    if (msg.type === 'progress') {
      for (const w of job.waiters) w.onProgress?.(msg.done, msg.total);
      return;
    }
    jobs.delete(msg.id);
    byKey.delete(job.key);
    if (msg.type === 'error') {
      const e = new Error(msg.message); e.name = msg.name;
      for (const w of job.waiters) { detach(w); w.reject(e); }
      return;
    }
    const mesh = msg.mesh;
    // compact index -> model atom index (worker computed over the packed arrays)
    for (let v = 0; v < mesh.atomIndex.length; v++) mesh.atomIndex[v] = job.atomIndices[mesh.atomIndex[v]!]!;
    put(job.key, mesh);
    for (const w of job.waiters) { detach(w); w.resolve(mesh); }
  };

  // Transport: a real Worker, or the handler in-thread.
  let worker: Worker | undefined;
  let post: (msg: ToWorker, transfer?: ArrayBuffer[]) => void;
  let mode: 'worker' | 'inline' = 'inline';
  if (options.worker !== false) {
    try {
      if (options.worker instanceof Object && 'postMessage' in options.worker) worker = options.worker as Worker;
      else if (typeof options.worker === 'function') worker = options.worker();
      else if (typeof Worker !== 'undefined') worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    } catch {
      worker = undefined;
    }
  }
  if (worker) {
    mode = 'worker';
    const w = worker;
    w.onmessage = (e: MessageEvent<FromWorker>) => onMessage(e.data);
    w.onerror = (e: ErrorEvent) => {
      const err = new Error(e.message || 'surface worker failed'); err.name = 'WorkerError';
      for (const job of jobs.values()) for (const wt of job.waiters) { detach(wt); wt.reject(err); }
      jobs.clear(); byKey.clear();
    };
    post = (msg, transfer) => w.postMessage(msg, transfer ?? []);
    if (options.wasmUrl || options.wasmBinary) {
      const init: ToWorker = { type: 'init' };
      if (options.wasmUrl) init.wasmUrl = options.wasmUrl;
      if (options.wasmBinary) init.wasmBinary = options.wasmBinary.slice(0);
      post(init);
    }
  } else {
    // In-thread fallback: the handler (and with it SESAM + Molstar's EDT) is loaded lazily so a browser bundle that
    // always has Worker keeps those modules in the worker chunk only. Messages queue on the same promise, so the
    // handler still sees them in order.
    let handle: Promise<(msg: ToWorker) => void> | undefined;
    post = (msg) => {
      handle ??= import('./worker').then((m) => m.createSurfaceWorkerHandler({ postMessage: (r) => onMessage(r) }, { transfer: false }));
      handle.then((h) => h(msg)).catch((e: unknown) => {
        if (msg.type !== 'compute') return;
        const err = e instanceof Error ? e : new Error(String(e));
        onMessage({ type: 'error', id: msg.id, name: err.name, message: err.message });
      });
    };
    if (options.wasmUrl || options.wasmBinary) {
      const init: ToWorker = { type: 'init' };
      if (options.wasmUrl) init.wasmUrl = options.wasmUrl;
      if (options.wasmBinary) init.wasmBinary = options.wasmBinary;
      post(init);
    }
  }

  function put(key: string, mesh: SurfaceMesh) {
    const bytes = meshBytes(mesh);
    const old = cache.get(key);
    if (old) { cache.delete(key); cacheBytes -= meshBytes(old); }
    cache.set(key, mesh);
    cacheBytes += bytes;
    while (cache.size > 1 && (cache.size > maxEntries || cacheBytes > maxBytes)) {
      const first = cache.keys().next().value as string;
      cacheBytes -= meshBytes(cache.get(first)!);
      cache.delete(first);
    }
    stats.entries = cache.size; stats.bytes = cacheBytes;
  }

  function touch(key: string): SurfaceMesh | undefined {
    const m = cache.get(key);
    if (!m) return undefined;
    cache.delete(key); cache.set(key, m);
    return m;
  }

  function detach(w: Waiter) {
    if (w.signal && w.onAbort) w.signal.removeEventListener('abort', w.onAbort);
  }

  function cancel(job: Job, w: Waiter) {
    job.waiters.delete(w);
    detach(w);
    if (job.waiters.size === 0) {
      jobs.delete(job.id); byKey.delete(job.key);
      post({ type: 'cancel', id: job.id });
    }
  }

  return {
    get mode() { return mode; },
    get stats() { return stats; },
    keyOf: (req) => packRequest(req).key,
    peek(req) {
      return touch(packRequest(req).key);
    },
    compute(req, opts = {}) {
      if (disposed) return Promise.reject(new Error('surface client disposed'));
      if (req.signal?.aborted) return Promise.reject(abortError());
      const packed = packRequest(req);
      const hit = touch(packed.key);
      if (hit) { stats.hits++; return Promise.resolve(hit); }
      stats.misses++;
      return new Promise<SurfaceMesh>((resolve, reject) => {
        const waiter: Waiter = { resolve, reject, onProgress: opts.onProgress, signal: req.signal };
        let job = byKey.get(packed.key);
        if (!job) {
          job = { id: nextId++, key: packed.key, atomIndices: packed.atomIndices, waiters: new Set() };
          jobs.set(job.id, job); byKey.set(job.key, job);
          post({ type: 'compute', id: job.id, req: packed.data }, packed.transfer);
        }
        job.waiters.add(waiter);
        if (req.signal) {
          const j = job;
          waiter.onAbort = () => { cancel(j, waiter); reject(abortError()); };
          req.signal.addEventListener('abort', waiter.onAbort, { once: true });
        }
      });
    },
    clearCache() { cache.clear(); cacheBytes = 0; stats.entries = 0; stats.bytes = 0; },
    dispose() {
      disposed = true;
      const e = abortError();
      for (const job of jobs.values()) for (const w of job.waiters) { detach(w); w.reject(e); }
      jobs.clear(); byKey.clear();
      worker?.terminate();
      cache.clear(); cacheBytes = 0; stats.entries = 0; stats.bytes = 0;
    },
  };
}
